import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useSessionQuery } from '@/hooks/use-auth';
import { Spinner } from '@/components/ui/spinner';
import { AppLayout } from '@/layouts/AppLayout';
import { LoginPage } from '@/pages/auth/LoginPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { UsersListPage } from '@/pages/settings/users/UsersListPage';
import { CreateUserPage } from '@/pages/settings/users/CreateUserPage';
import { UserProfilePage } from '@/pages/settings/users/UserProfilePage';
import { UserPermissionsPage } from '@/pages/settings/users/UserPermissionsPage';
import { ClientsListPage } from '@/pages/clients/ClientsListPage';
import { CreateClientPage } from '@/pages/clients/CreateClientPage';
import { ClientDetailPage } from '@/pages/clients/ClientDetailPage';
import { EditClientPage } from '@/pages/clients/EditClientPage';
import { HotelsListPage } from '@/pages/hotels/HotelsListPage';
import { CreateHotelPage } from '@/pages/hotels/CreateHotelPage';
import { HotelDetailPage } from '@/pages/hotels/HotelDetailPage';
import { EditHotelPage } from '@/pages/hotels/EditHotelPage';
import { RatesListPage } from '@/pages/rates/RatesListPage';
import { CreateRatePage } from '@/pages/rates/CreateRatePage';
import { EditRatePage } from '@/pages/rates/EditRatePage';
import { BookingsListPage } from '@/pages/bookings/BookingsListPage';
import { BookingDetailPage } from '@/pages/bookings/BookingDetailPage';
import { NewBookingPage } from '@/pages/bookings/NewBookingPage';
import { EditBookingPage } from '@/pages/bookings/EditBookingPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { ReportsPage } from '@/pages/reports/ReportsPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isPending, data } = useSessionQuery();
  if (isPending) return <div className="flex h-screen items-center justify-center"><Spinner className="h-6 w-6" /></div>;
  if (!data) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  {
    path: '/',
    element: <ProtectedRoute><AppLayout /></ProtectedRoute>,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: <DashboardPage /> },
      { path: 'clients', element: <ClientsListPage /> },
      { path: 'clients/new', element: <CreateClientPage /> },
      { path: 'clients/:id', element: <ClientDetailPage /> },
      { path: 'clients/:id/edit', element: <EditClientPage /> },
      { path: 'hotels', element: <HotelsListPage /> },
      { path: 'hotels/new', element: <CreateHotelPage /> },
      { path: 'hotels/:id', element: <HotelDetailPage /> },
      { path: 'hotels/:id/edit', element: <EditHotelPage /> },
      { path: 'rates', element: <RatesListPage /> },
      { path: 'rates/new', element: <CreateRatePage /> },
      { path: 'rates/:id/edit', element: <EditRatePage /> },
      { path: 'reports', element: <ReportsPage /> },
      { path: 'bookings', element: <BookingsListPage /> },
      { path: 'bookings/new', element: <NewBookingPage /> },
      { path: 'bookings/:id', element: <BookingDetailPage /> },
      { path: 'bookings/:id/edit', element: <EditBookingPage /> },
      { path: 'settings/users', element: <UsersListPage /> },
      { path: 'settings/users/new', element: <CreateUserPage /> },
      { path: 'settings/users/:id', element: <UserProfilePage /> },
      { path: 'settings/users/:id/permissions', element: <UserPermissionsPage /> },
    ],
  },
]);

export default function App() {
  // Fire the session query at the root so ProtectedRoute's call is free (TanStack deduplicates)
  useSessionQuery();

  return (
    <>
      <RouterProvider router={router} />
      <Toaster position="top-right" richColors />
    </>
  );
}
